import { useEffect, useRef, useState } from 'react';

import { cn } from '@cobliteam/ui/utils';

import { Analytics } from '@cobliteam/analytics';
import { type MediaEvent, MediaType, type MediaView } from '@cobliteam/common-types';
import Button from '@cobliteam/ui/components/Button';
import Tooltip from '@cobliteam/ui/components/Tooltip';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import * as Slider from '@radix-ui/react-slider';

interface MediaPlayerButtonProps {
  iconName: IconNames;
  tooltipText: string;
  onClick: () => void;
  iconClassName?: string;
}

const MediaPlayerButton = ({ iconName, tooltipText, onClick, iconClassName }: MediaPlayerButtonProps) => (
  <Tooltip content={tooltipText}>
    <Button onClick={onClick} variant='text' className='p-0 hover:bg-transparent'>
      <Icon size='md' name={iconName} className={cn(iconClassName || '', 'cursor-pointer text-gray-8 hover:text-white')} />
    </Button>
  </Tooltip>
);

export interface MediaVideoPlayerProps {
  mediaView: MediaView;
}

const MediaVideoPlayer = ({ mediaView }: MediaVideoPlayerProps) => {
  const videos = mediaView.videos;
  const playerRefs = videos.map((_) => useRef<HTMLVideoElement | null>(null), []);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [videoTimestamp, setVideoTimestamp] = useState<number>(0);
  const [videoDuration, setVideoDuration] = useState<number>(0);

  const [updateInterval, setUpdateInterval] = useState<NodeJS.Timeout | undefined>(undefined);
  const hasMultipleVideos = videos.length > 1;
  const mediaInfo = mediaView.mediaInfo;
  const thumbnail = hasMultipleVideos ? undefined : mediaView.mediaInfo.mediaDetails.thumbnailUrl;

  useEffect(() => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }

    setUpdateInterval(
      setInterval(() => {
        const player = playerRefs[0].current;
        if (player && !Number.isNaN(player.duration)) {
          setVideoTimestamp(Math.round(player.currentTime));
          setVideoDuration(Math.round(player.duration));
        }
      }, 500)
    );
  }, [playerRefs[0].current?.duration]);

  const handlePlay = () => {
    playerRefs.forEach((playerRef) => playerRef.current?.play());
    setIsPlaying(true);

    const path = origin;
    const mediaType = mediaInfo?.mediaType;
    const isFavorite = mediaInfo?.isFavorite;
    const eventType = mediaType === MediaType.EVENT_VIDEO ? (mediaInfo.mediaDetails as MediaEvent).eventTrigger : mediaType;
    const eventSeverity = mediaInfo.severity;

    Analytics.track('Dashcam Video Played', eventSeverity ? { path, eventType, eventSeverity, isFavorite } : { path, eventType, isFavorite });
  };

  const handlePause = () => {
    playerRefs.forEach((playerRef) => playerRef.current?.pause());
    setIsPlaying(false);
  };

  const handleEnd = () => {
    setIsPlaying(false);

    const path = origin;
    const mediaType = mediaInfo?.mediaType;
    const eventType = mediaType === MediaType.EVENT_VIDEO ? (mediaInfo.mediaDetails as MediaEvent).eventTrigger : mediaType;
    const eventSeverity = mediaInfo.severity;

    Analytics.track('Dashcam Video Watched Fully', eventSeverity ? { path, eventType, eventSeverity } : { path, eventType });
  };

  const updateVideoTime = (newVideoTime: number) => {
    playerRefs.forEach((playerRef) => {
      if (playerRef.current) {
        playerRef.current.currentTime = newVideoTime;
      }
    });
  };

  const handleSliderchange = (newValue: readonly number[]) => {
    updateVideoTime(newValue[0]);
    setVideoTimestamp(newValue[0]);
  };

  const handleRewind = () => {
    if (videoDuration) {
      const newVideoTime = Math.max(0, videoTimestamp - 5);
      setVideoTimestamp(newVideoTime);
      updateVideoTime(newVideoTime);
    }
  };

  const handleForward = () => {
    if (videoDuration) {
      const newVideoTime = Math.min(videoDuration, videoTimestamp + 5);
      setVideoTimestamp(newVideoTime);
      updateVideoTime(newVideoTime);
    }
  };

  const formatSeconds = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div data-testid='media-video-player' className='flex aspect-video w-full items-center justify-center rounded'>
      <div className='flex flex-col gap-3 rounded bg-black p-5'>
        <div className={cn('flex overflow-hidden rounded border-box bg-gray-light', hasMultipleVideos ? 'grid grid-cols-2' : '')}>
          {videos.map((video, index) => (
            <div key={index} className='relative'>
              <video
                ref={playerRefs[index]}
                key={video.url}
                className={cn(hasMultipleVideos ? 'grid h-auto w-[430px] grid-cols-subgrid' : 'h-auto w-[860px]', 'cursor-pointer')}
                onEnded={handleEnd}
                poster={thumbnail}
                onClick={isPlaying ? handlePause : handlePlay}
                muted
              >
                <source src={video.url} type='video/mp4' />
              </video>
              <Button onClick={() => playerRefs[index].current?.requestFullscreen()} variant='text' className='absolute right-2 bottom-1 p-0 hover:bg-transparent'>
                <Icon size='md' name='fullscreen' className='cursor-pointer text-gray-8 hover:text-white' />
              </Button>
            </div>
          ))}
        </div>
        <div>
          <div className='flex items-center gap-2'>
            <MediaPlayerButton iconName='rewind' tooltipText={'Recuar 5s'} onClick={handleRewind} />
            <MediaPlayerButton iconName={isPlaying ? 'pause' : 'play'} tooltipText={isPlaying ? 'Parar' : 'Executar'} onClick={isPlaying ? handlePause : handlePlay} />
            <MediaPlayerButton iconName='rewind' tooltipText={'Avançar 5s'} onClick={handleForward} iconClassName='rotate-180' />

            {videoDuration > 0 && (
              <div className='pl-2'>
                <Typography variant='description-list-medium' color='white'>
                  {formatSeconds(videoTimestamp)}/{formatSeconds(videoDuration)}
                </Typography>
              </div>
            )}
          </div>
          <form>
            <Slider.Root
              className='relative flex h-5 w-full cursor-pointer touch-none select-none items-center'
              defaultValue={[0]}
              value={[videoTimestamp]}
              max={videoDuration}
              step={1}
              onValueChange={handleSliderchange}
            >
              <Slider.Track className='relative h-1.5 grow rounded bg-gray-8'>
                <Slider.Range className='absolute h-full bg-primary' />
              </Slider.Track>
            </Slider.Root>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MediaVideoPlayer;
